/**
 * The checks that must pass before a wallet is asked to sign an approve or a
 * swap.
 *
 * Two contract addresses reach the transaction over the network — the swap
 * contract and the legacy token to approve — and the network is the part an
 * attacker gets to influence. Each function here refuses one way that can go
 * wrong.
 *
 * Neither is the last line of defence: `swap()` reverts for a denylisted
 * account or one over its wallet cap regardless of what the UI showed. What
 * these prevent is the step before that: a user opening their wallet at all,
 * and paying gas, for a transaction that is not the one the page described.
 *
 * Pure and React-free on purpose, so the rules can be tested without a browser
 * — the same reason `lib/wallets.ts` is shaped this way.
 */
import { getAddress, isAddress, type Address } from "viem";

const REPORT = "Jangan lanjutkan dan laporkan ke kanal support resmi.";

/** Case-insensitive address comparison that never throws on bad input. */
function sameAddress(a: string, b: string): boolean {
  if (!isAddress(a, { strict: false }) || !isAddress(b, { strict: false })) return false;
  return getAddress(a) === getAddress(b);
}

/**
 * The swap contract, taken from the build rather than from the API.
 *
 * `NEXT_PUBLIC_CLAIM_CONTRACT` is baked into the bundle that the nonce-based
 * CSP in `proxy.ts` protects; a copy returned by an API response is not. Signing
 * against an API-supplied address would route around that CSP entirely —
 * anyone able to influence one JSON response could point a swap at a contract
 * of their choosing.
 *
 * @returns an error message, or `null` when it is safe to proceed.
 */
export function checkClaimContract(
  bundled: Address | null,
  fromApi: string | null,
): string | null {
  if (!bundled) return "Kontrak klaim belum dikonfigurasi.";
  if (fromApi && !sameAddress(fromApi, bundled)) {
    return `Alamat kontrak klaim dari server tidak cocok dengan yang tertanam di portal. ${REPORT}`;
  }
  return null;
}

/**
 * The legacy token about to be approved, taken from the build rather than
 * from the API — same reasoning as {@link checkClaimContract}, applied to the
 * `approve()` call instead of the `swap()` call. An attacker who could steer
 * this address would not steal funds directly (approve moves nothing by
 * itself), but they could get a user to authorise spending on a token that
 * is not the real legacy AsetQu, wasting gas on an approval that helps no one.
 */
export function checkLegacyToken(
  bundled: Address | null,
  fromApi: string | null,
): string | null {
  if (!bundled) return "Kontrak token lama belum dikonfigurasi.";
  if (fromApi && !sameAddress(fromApi, bundled)) {
    return `Alamat token lama dari server tidak cocok dengan yang tertanam di portal. ${REPORT}`;
  }
  return null;
}
